import React, { CSSProperties, forwardRef, MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import camelCase from "lodash-es/camelCase";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import EntityChip from "@app/component/EntityChip";
import configService from "@app/config/ConfigService";
import authService from "@app/store/auth/AuthService";
import { TimelineEventRouteCreated } from "@app/types/events/TimelineEvent";
import { getLegacyAppUrl } from "@app/util/appUrl";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventRouteCreated;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {}

const RouteCreatedEvent = forwardRef<HTMLDivElement, Props>(
  ({ event, expanded, onSizeChange, style }, ref) => {
    const intl = useIntl();

    const route = event.entity;

    const baseOldAppUrl = configService.getBaseOldAppUrl();
    const token = authService.getToken();
    const handleOpenRoute = useCallback(
      (_, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        if (token) {
          window.open(
            getLegacyAppUrl(
              baseOldAppUrl,
              token,
              route.routeType === EntityType.COMPANY_ROUTE
                ? `/accounts/routing/edit/${route.id}`
                : `/contacts/routing/edit/${route.id}`
            ),
            "_blank"
          );
        }
      },
      [baseOldAppUrl, token, route]
    );

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.routeCreated",
            defaultMessage:
              "created a {type, select, accountRoutes {Company Route} contactRoutes {Person Route} other {Route}} {name}",
            description: "Title of the Route Created event item",
          },
          {
            name: (
              <EntityChip
                entityId={route.id}
                entityType={EntityType.ROUTE}
                inline
                name={route.name}
                onClick={handleOpenRoute}
              />
            ),
            type: camelCase(event.entity.routeType),
          }
        )}
        user={event.updatedBy}
      >
        <div className={styles.grid}>
          <div>
            {intl.formatMessage({
              id: "dashboard.timeline.event.routeCreated.data.startsAt",
              defaultMessage: "Starts at",
              description:
                "Start position row in Route details grid in the Route Created event card",
            })}
          </div>
          <div>{route.routeDetail.startAddress}</div>
          <div>
            {intl.formatMessage({
              id: "dashboard.timeline.event.routeCreated.data.stops",
              defaultMessage: "Stops",
              description: "Stops row in Route details grid in the Route Created event card",
            })}
          </div>
          <div>{route.items}</div>
        </div>
      </BaseEvent>
    );
  }
);

const mapStateToProps = () => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  RouteCreatedEvent
);
